import { defineModule } from '../../engine/module';
import { ProjectDetailScreen } from './ProjectDetailScreen';
import { WorkloadScreen } from './WorkloadScreen';
import { FromTemplateAction } from './FromTemplateAction';
import { LogTimeOnTaskAction } from './LogTimeOnTaskAction';
import { ProjectProgress, TaskEffort } from './widgets';
import './projects.css';

// Projects UI (docs/modules/projects.md): projects list (table + board), the project
// workspace (custom: five task views + budget/plan), my tasks, workload, templates. Everything
// else is the engine rendering the module's ResourceDefs.

export const projectsModule = defineModule({
  name: 'projects',
  nav: [
    {
      label: 'Projects',
      order: 25,
      items: [
        { label: 'Projects', to: '/projects', icon: 'folder-open', key: 'p', permission: 'projects:list' },
        { label: 'My tasks', to: '/my-tasks', icon: 'check-square', key: 'y', permission: 'my-tasks:list' },
        { label: 'Workload', to: '/workload', icon: 'users', key: 'w', permission: 'workload:read' },
      ],
    },
    {
      label: 'Projects',
      area: 'config',
      order: 40,
      items: [{ label: 'Project templates', to: '/project-templates', icon: 'file-text', permission: 'project-templates:list' }],
    },
  ],
  screens: [
    {
      path: '/projects',
      resource: 'projects',
      kind: 'list',
      title: 'Projects',
      subtitle: 'Client and internal work with phases, tasks, budgets and time.',
      columns: ['ref', 'name', 'client_id', 'status', 'health', 'owner_tech_id', 'target_end_date', 'progress_percent'],
      filters: [{ field: 'status', kind: 'chips' }, { field: 'health', kind: 'chips' }],
      views: ['table', 'board'],
      board: { groupBy: 'status', cardFields: ['client_id', 'health', 'target_end_date', 'progress_percent'] },
      query: { filter: [{ field: 'archived_at', op: 'is_null', value: true }] },
      actions: [{ name: 'from-template', label: 'New from template', scope: 'collection', component: FromTemplateAction, openParam: 'template' }],
      form: { fields: ['name', 'client_id', 'owner_tech_id', 'status', 'priority', 'start_date', 'target_end_date', 'currency', 'description'] },
      detailPath: '/projects/:id',
    },
    { path: '/projects/:id', resource: 'projects', kind: 'custom', title: 'Project', component: ProjectDetailScreen },
    {
      path: '/my-tasks',
      resource: 'my-tasks',
      kind: 'list',
      title: 'My tasks',
      subtitle: 'Tasks assigned to you across every project.',
      columns: ['ref', 'title', 'project_id', 'status', 'priority', 'due_date', 'estimate_minutes', 'actual_minutes'],
      filters: [{ field: 'status', kind: 'chips' }],
      views: ['table', 'board', 'calendar'],
      board: { groupBy: 'status', cardFields: ['project_id', 'due_date'] },
      calendar: { dateField: 'due_date' },
      actions: [{ name: 'log-time', label: 'Log time', scope: 'collection', component: LogTimeOnTaskAction, openParam: 'log' }],
      form: { fields: ['project_id', 'title', 'phase_id', 'kind', 'priority', 'assignee_tech_id', 'start_date', 'due_date', 'estimate_minutes', 'description'] },
      inspector: true,
      detailPath: null,
    },
    { path: '/workload', resource: '', kind: 'custom', title: 'Workload', component: WorkloadScreen },
    {
      path: '/project-templates',
      resource: 'project-templates',
      kind: 'list',
      title: 'Project templates',
      subtitle: 'Reusable plans: phases, tasks with offsets, creation rules and dependencies.',
      columns: ['name', 'default_priority', 'is_active', 'updated_at'],
      filters: [{ field: 'is_active', kind: 'toggle' }],
      form: { fields: ['name', 'description', 'default_priority', 'portal_visible', 'is_active'] },
    },
    {
      path: '/project-templates/:id',
      resource: 'project-templates',
      kind: 'detail',
      builtinTabs: ['overview', 'activity'],
      tabs: [
        { key: 'phases', label: 'Phases', icon: 'list-tree', resource: 'project-template-phases', filter: { template_id: ':id' }, columns: ['order', 'name', 'offset_days', 'duration_days', 'colour'] },
        { key: 'tasks', label: 'Tasks', icon: 'check-square', resource: 'project-template-tasks', filter: { template_id: ':id' }, columns: ['order', 'key', 'title', 'phase_id', 'kind', 'estimate_minutes', 'offset_days', 'duration_days', 'parent_key'] },
      ],
    },
  ],
  widgets: { 'project-progress': ProjectProgress, 'task-effort': TaskEffort },
  commands: [
    { id: 'new-project', label: 'New project…', kind: 'create', icon: 'folder-open', keywords: 'project create', run: ({ navigate }) => navigate('/projects?new=1') },
    { id: 'new-project-template', label: 'New project from template…', kind: 'create', icon: 'file-text', keywords: 'project template onboarding', run: ({ navigate }) => navigate('/projects?template=1') },
    { id: 'new-task', label: 'New task…', kind: 'create', icon: 'check-square', keywords: 'task todo', run: ({ navigate }) => navigate('/my-tasks?new=1') },
    { id: 'log-time-task', label: 'Log time on task…', kind: 'create', icon: 'timer', keywords: 'time task minutes hours', run: ({ navigate }) => navigate('/my-tasks?log=1') },
    { id: 'workload', label: 'Workload', kind: 'go to', icon: 'users', keywords: 'capacity technicians weeks', run: ({ navigate }) => navigate('/workload') },
  ],
});
